import { useEffect, useState } from "react";
import { Route, Switch, useLocation, useSearch } from "wouter";

import { apiClient } from "./api/client.js";
import { AuthProvider, useAuth } from "./auth/AuthContext.js";
import { takeRememberedReturnTo } from "./lib/returnTo.js";
import { ContentBundle } from "./pages/ContentBundle.js";
import { DeviceCliCallback } from "./pages/DeviceCliCallback.js";
import { DeviceDetail } from "./pages/DeviceDetail.js";
import { DeviceEvents } from "./pages/DeviceEvents.js";
import { Devices } from "./pages/Devices.js";
import { FleetDetail } from "./pages/FleetDetail.js";
import { Fleets } from "./pages/Fleets.js";
import { Home } from "./pages/Home.js";
import { KioskReleases } from "./pages/KioskReleases.js";
import { Login } from "./pages/Login.js";
import { Pricing } from "./pages/Pricing.js";
import { Sessions } from "./pages/Sessions.js";
import { VenueDetail } from "./pages/VenueDetail.js";
import { Venues } from "./pages/Venues.js";

function Header() {
  const { user, logout } = useAuth();
  const [location] = useLocation();
  const analyticsUrl = window.serverConfig?.ANALYTICS_URL;

  return (
    <header className="flex items-center justify-between px-6 py-3 border-b border-border">
      <div className="flex items-center gap-6">
        <a
          href="/"
          className="text-lg font-bold text-text-primary no-underline hover:text-accent transition-colors"
        >
          TeamTable Admin
        </a>
        <nav className="flex items-center gap-4">
          <a
            href="/"
            className={`text-sm no-underline transition-colors ${
              location === "/"
                ? "text-accent font-medium"
                : "text-text-muted hover:text-text-primary"
            }`}
          >
            Dashboard
          </a>
          <a
            href="/venues"
            className={`text-sm no-underline transition-colors ${
              location.startsWith("/venues")
                ? "text-accent font-medium"
                : "text-text-muted hover:text-text-primary"
            }`}
          >
            Venues
          </a>
          <a
            href="/fleets"
            className={`text-sm no-underline transition-colors ${
              location.startsWith("/fleets")
                ? "text-accent font-medium"
                : "text-text-muted hover:text-text-primary"
            }`}
          >
            Fleets
          </a>
          <a
            href="/devices"
            className={`text-sm no-underline transition-colors ${
              location.startsWith("/devices")
                ? "text-accent font-medium"
                : "text-text-muted hover:text-text-primary"
            }`}
          >
            Devices
          </a>
          <a
            href="/sessions"
            className={`text-sm no-underline transition-colors ${
              location === "/sessions"
                ? "text-accent font-medium"
                : "text-text-muted hover:text-text-primary"
            }`}
          >
            Sessions
          </a>
          <a
            href="/pricing"
            className={`text-sm no-underline transition-colors ${
              location === "/pricing"
                ? "text-accent font-medium"
                : "text-text-muted hover:text-text-primary"
            }`}
          >
            Pricing
          </a>
          {user?.is_platform_admin && (
            <a
              href="/content-bundle"
              className={`text-sm no-underline transition-colors ${
                location === "/content-bundle"
                  ? "text-accent font-medium"
                  : "text-text-muted hover:text-text-primary"
              }`}
            >
              Content
            </a>
          )}
          {user?.is_platform_admin && (
            <a
              href="/kiosk-releases"
              className={`text-sm no-underline transition-colors ${
                location === "/kiosk-releases"
                  ? "text-accent font-medium"
                  : "text-text-muted hover:text-text-primary"
              }`}
            >
              Kiosk
            </a>
          )}
          {/**
           * A different app on a sibling host, so a plain link rather than a route. Fleet admins
           * see it too — analytics scopes itself to their own fleets.
           */}
          {analyticsUrl && (
            <a
              href={analyticsUrl}
              className="text-sm text-text-muted no-underline transition-colors hover:text-text-primary"
            >
              Analytics
            </a>
          )}
        </nav>
      </div>
      {user && (
        <div className="flex items-center gap-3">
          <span className="text-sm text-text-secondary">{user.display_name}</span>
          <button
            onClick={logout}
            className="text-sm text-text-muted hover:text-text-primary transition-colors"
          >
            Sign out
          </button>
        </div>
      )}
    </header>
  );
}

function VerifyPage() {
  const [, navigate] = useLocation();
  const search = useSearch();
  const { refresh } = useAuth();
  const [error, setError] = useState("");
  const token = new URLSearchParams(search).get("token");

  useEffect(() => {
    if (!token) {
      setError("No token provided");
      return;
    }

    apiClient["/api/v1/admin/auth/verify-link"]
      ["post"]({
        parameters: {},
        body: { token },
      })
      .then(async (res) => {
        if (res.code === 200) {
          await refresh();
          /** Set aside when a sibling app sent them to sign in; the email link drops the query. */
          const external = takeRememberedReturnTo();
          if (external) {
            window.location.assign(external);
            return;
          }
          navigate("/");
        } else {
          setError("Invalid or expired token");
        }
      })
      .catch(() => setError("Verification failed"));
  }, [token, navigate, refresh]);

  if (error) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-red-500">{error}</p>
          <a href="/" className="mt-4 inline-block text-sm text-accent hover:underline">
            Back to sign in
          </a>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center">
      <p className="text-text-secondary">Verifying...</p>
    </main>
  );
}

function AppContent() {
  const { user, loading, accessDenied, logout } = useAuth();
  const [location] = useLocation();
  const search = useSearch();

  if (location.startsWith("/auth/verify")) {
    return <VerifyPage />;
  }

  if (location.startsWith("/auth/device-cli")) {
    // Device CLI callback — show login if not authenticated, otherwise handle callback
    if (loading) return null;
    if (!user) return <Login returnTo={location + (search ? `?${search}` : "")} />;
    return <DeviceCliCallback />;
  }

  if (loading) {
    return null;
  }

  if (accessDenied) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <h1 className="text-xl font-bold text-text-primary">Access Denied</h1>
        <p className="text-text-secondary">You do not have admin or fleet access.</p>
        <button
          onClick={logout}
          className="px-4 py-2 rounded bg-accent text-white hover:bg-accent-hover transition-colors"
        >
          Sign out
        </button>
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  return (
    <>
      <Header />
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/venues" component={Venues} />
        <Route path="/venues/:slug" component={VenueDetail} />
        <Route path="/fleets" component={Fleets} />
        <Route path="/fleets/:id" component={FleetDetail} />
        <Route path="/devices" component={Devices} />
        <Route path="/sessions" component={Sessions} />
        <Route path="/pricing" component={Pricing} />
        <Route path="/content-bundle" component={ContentBundle} />
        <Route path="/kiosk-releases" component={KioskReleases} />
        <Route path="/devices/:id/events" component={DeviceEvents} />
        <Route path="/devices/:id" component={DeviceDetail} />
      </Switch>
    </>
  );
}

export function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
